import { useEffect, useState } from 'react';
import type { ConsumerDropOff } from '../types/dtos';
import { Markdown } from './Markdown';
import { useT } from '../hooks/useT';

type Props = {
  config: ConsumerDropOff;
  selected: boolean;
  locationOptionId: string | null;
  note: string;
  termsAccepted: boolean;
  onSelect: () => void;
  onLocationChange: (id: string | null) => void;
  onNoteChange: (note: string) => void;
  onTermsChange: (accepted: boolean) => void;
};

export function DropOffAuthorizationOption({
  config, selected, locationOptionId, note, termsAccepted,
  onSelect, onLocationChange, onNoteChange, onTermsChange,
}: Props) {
  const { t } = useT();
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    if (!termsOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setTermsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [termsOpen]);

  return (
    <div
      className={[
        'mt-2 rounded-lg border p-3 transition',
        selected ? 'border-brand ring-2 ring-brand-ring' : 'border-slate-300 bg-white hover:border-brand',
      ].join(' ')}
    >
      <label className="flex cursor-pointer items-center gap-3">
        <input
          type="radio"
          name="delivery-choice"
          value="dropoff"
          checked={selected}
          onChange={onSelect}
          className="h-4 w-4 text-brand focus:ring-brand-ring"
        />
        <span className="font-medium">{t('offer.dropoff_grant')}</span>
      </label>

      {selected && (
        <div className="mt-3 space-y-3 pl-7">
          <div>
            <label htmlFor="dropoff-location" className="mb-1 block text-sm font-medium text-slate-700">
              {t('offer.dropoff_location')}
            </label>
            <select
              id="dropoff-location"
              value={locationOptionId ?? ''}
              onChange={(e) => onLocationChange(e.target.value || null)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand-ring"
            >
              <option value="">{t('offer.dropoff_location_none')}</option>
              {config.locationOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="dropoff-note" className="mb-1 block text-sm font-medium text-slate-700">
              {t('offer.dropoff_note')}
            </label>
            <textarea
              id="dropoff-note"
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder={t('offer.dropoff_note_placeholder')}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand-ring"
            />
          </div>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => onTermsChange(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-brand focus:ring-brand-ring"
            />
            <span>
              {t('offer.dropoff_accept_prefix')}{' '}
              <button
                type="button"
                onClick={() => setTermsOpen(true)}
                className="text-brand underline"
              >
                {t('offer.dropoff_terms_link')}
              </button>
            </span>
          </label>
        </div>
      )}

      {termsOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('offer.dropoff_terms_link')}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setTermsOpen(false); }}
        >
          <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl ring-1 ring-slate-200">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">{t('offer.dropoff_terms_link')}</h2>
              <button
                type="button"
                onClick={() => setTermsOpen(false)}
                aria-label={t('offer.dropoff_terms_close')}
                className="rounded border border-slate-300 px-2 py-1 text-sm"
              >
                ✕
              </button>
            </div>
            <Markdown source={config.termsBody} />
          </div>
        </div>
      )}
    </div>
  );
}
